<template>
  <section class="emh-side-filters-dealer-filter">
    <template v-if="!showDealerSelector">
      <div
        v-if="selectedDealer"
        class="emh-side-filters-dealer-filter__dealer-info emh-side-filter-dealer-info"
        data-test-id="emh-side-filters-dealer-filter__dealer-info"
      >
        <div class="emh-side-filter-dealer-info__dealer-name-info">
          <h4
            class="emh-side-filter-dealer-info__dealer-title wb-type-copy-strong"
            data-test-id="emh-side-filter-dealer-info__dealer-title"
          >
            {{ selectedDealer.name }}
          </h4>
          <button
            class="wb-round-button wb-round-button--solid-level-0 wb-round-button--small"
            type="button"
            aria-label="Reset dealer"
            data-test-id="emh-side-filter-dealer-info__dealer-reset-button"
            :disabled="isLoading"
            @click="resetDealerFilter"
          >
            <wb-icon class="wb-round-button__icon" aria-hidden="true" :name.attr="'close'" flip-rtl></wb-icon>
          </button>
        </div>

        <hr class="emh-side-filter-dealer-info__divider" />

        <emh-side-filters-dealer-contact-info
          class="emh-side-filter-dealer-info__contact"
          data-test-id="emh-side-filter-dealer-info__contact"
          :dealer-info="selectedDealer"
        />
      </div>

      <button
        class="emh-side-filters-dealer-filter__select-dealer-button wb-button wb-button--secondary wb-button--large"
        data-test-id="emh-side-filters-dealer-filter__select-dealer-button"
        :disabled="isLoading"
        @click="toggleDealerSelectorModal"
      >
        <wb-icon class="wb-button__icon" aria-hidden="true" flip-rtl :name.attr="'bds/location/16'"></wb-icon>
        {{ $t('dcp.srp.filters.dealerSearch.' + (selectedDealer ? 'changeDealer' : 'selectDealer')) }}
      </button>
    </template>

    <dealer-locator-selector
      v-if="showDealerSelector && !shouldBeModal"
      :interactive="true"
      :vendors="dealerIds"
      :allowed-list="allowedList"
      apply-allowed-list-enabled
      loading="eager"
      data-test-id="emh-side-filters-dealer-filter__dealer-selector"
      @vendor-selected-all="selectVendor"
    />

    <Teleport v-if="srpModalTarget" :to="srpModalTarget">
      <dealer-selection-modal
        class="emh-side-filters-dealer-filter__dealer-selector-modal dcp-frame-footer-modal"
        data-test-id="emh-side-filters-dealer-filter__dealer-selector-modal"
        :show="showDealerSelector && shouldBeModal"
        :dealer-ids="dealerIds"
        :allowed-list="allowedList"
        apply-allowed-list-enabled
        @close="toggleDealerSelectorModal"
        @vendor-selected="selectVendor"
      />
    </Teleport>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'EmhSideFiltersDealerFilter',
});
</script>

<script lang="ts" setup>
import { DealerEntry, DealerInfo } from '@dh-io-emh/shared/src/models/dealer.model';
import { computed, ComputedRef, PropType, ref, toRefs } from 'vue';
import EmhSideFiltersDealerContactInfo from './sideFiltersDealerContactInfo/SideFiltersDealerContactInfo.vue';
import DealerSelectionModal from '@dh-io-emh/shared/src/components/dealerSelectionModal/DealerSelectionModal.vue';
import { useSrpModalTargets } from '../../../../composables/srp-modals.composable';
import DealerLocatorSelector from '@dh-io-emh/shared/src/components//utils/dealerLocatorSelector/index.vue';
import { useBreakpoint } from '@dh-io-emh/shared/src/composables/breakpoint.composable';

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  selectedDealer: {
    type: Object as PropType<DealerInfo | null>,
    default: null,
  },
  dealers: {
    type: Array as PropType<DealerEntry[]>,
    default: () => [],
  },
  allowedList: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const emit = defineEmits(['vendor-selected', 'reset']);

const { dealers } = toRefs(props);

const dealerIds: ComputedRef<Array<string>> = computed(() => dealers.value.map(({ value }) => value));

const { breakpoint } = useBreakpoint();

const shouldBeModal = computed(() => !['mq1', 'mq2', 'mq3', 'mq4'].includes(breakpoint.value));

const showDealerSelector = ref(false);

const { srpModalTarget } = useSrpModalTargets();

const selectVendor = (vendor: any): void => {
  toggleDealerSelectorModal();
  emit('vendor-selected', vendor);
};

const resetDealerFilter = (): void => {
  emit('reset');
};

const toggleDealerSelectorModal = (): void => {
  showDealerSelector.value = !showDealerSelector.value;
};
</script>

<style lang="scss" scoped>
.emh-side-filters-dealer-filter {
  &__select-dealer-button {
    width: 100%;
  }

  &__dealer-selector-modal {
    :deep(.emh-dealer-locator-selector) {
      min-height: 720px;
      height: auto;
    }
  }
}

.emh-side-filter-dealer-info {
  @include wb-spacing('margin', 'xs 0 m 0');

  &__dealer-name-info {
    @include wb-spacing('margin-bottom', 's');

    display: flex;
    justify-content: space-between;
  }

  &__dealer-title {
    @include wb-spacing('margin-right', 'xs');

    align-self: center;
    color: var(--wb-grey-20);
  }

  &__divider {
    @include wb-spacing('margin', '0 0 s 0');

    border: 0;
    background-color: var(--wb-grey-85);
    height: 1px;
  }

  &__contact {
    @include wb-spacing('padding', '0 xs');
  }
}
</style>
